// src/components/resources/calculators/ABTestCalculator.logic.ts
//
// Pure statistical logic for the A/B test significance calculator, extracted from
// the client component so it can be unit-tested without pulling the React/email
// (env) import graph. See ABTestCalculator.logic.test.ts.

/** Inputs for the A/B test statistical-significance calculator. */
export interface CalculatorInputs {
  controlVisitors: number
  controlConversions: number
  variantVisitors: number
  variantConversions: number
}

/** Computed A/B test results. */
export interface CalculatorResults {
  controlRate: number
  variantRate: number
  relativeChange: number
  absoluteChange: number
  pValue: number
  confidenceLevel: number
  isSignificant: boolean
  recommendedSampleSize: number
}

export function calculateStandardError(rate: number, sampleSize: number): number {
  return Math.sqrt((rate * (1 - rate)) / sampleSize)
}

export function calculateZScore(rate1: number, rate2: number, se1: number, se2: number): number {
  return Math.abs(rate1 - rate2) / Math.sqrt(se1 * se1 + se2 * se2)
}

export function calculatePValue(zScore: number): number {
  // `p` is the upper-tail area Q(z) = 1 − Φ(z) (Zelen-Severo approximation of the
  // standard normal). The two-tailed p-value is 2·Q(z). The previous
  // `2 * (1 - (0.5 - p))` simplified to 1 + 2p, which made
  // `confidenceLevel = (1 - pValue) * 100` ALWAYS negative (e.g. z=1.96 → −5%).
  const t = 1 / (1 + 0.2316419 * Math.abs(zScore))
  const d = 0.3989423 * Math.exp(-zScore * zScore / 2)
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))))
  return 2 * p
}

export function calculateRecommendedSampleSize(rate: number, mde = 0.1): number {
  const z_alpha = 1.96 // 95% confidence level
  const z_beta = 0.84 // 80% power
  const p1 = rate
  const p2 = rate * (1 + mde)
  const p_avg = (p1 + p2) / 2

  const size = Math.ceil(
    2 * ((z_alpha + z_beta) ** 2) * p_avg * (1 - p_avg) / ((p2 - p1) ** 2),
  )

  return size
}

export function calculateMetrics(inputs: CalculatorInputs): CalculatorResults {
  const controlRate = inputs.controlConversions / inputs.controlVisitors
  const variantRate = inputs.variantConversions / inputs.variantVisitors

  const se1 = calculateStandardError(controlRate, inputs.controlVisitors)
  const se2 = calculateStandardError(variantRate, inputs.variantVisitors)
  const zScore = calculateZScore(controlRate, variantRate, se1, se2)
  const pValue = calculatePValue(zScore)

  const relativeChange = ((variantRate - controlRate) / controlRate) * 100
  const absoluteChange = (variantRate - controlRate) * 100
  const confidenceLevel = (1 - pValue) * 100
  const isSignificant = confidenceLevel >= 95

  const recommendedSampleSize = calculateRecommendedSampleSize(controlRate)

  return {
    controlRate,
    variantRate,
    relativeChange,
    absoluteChange,
    pValue,
    confidenceLevel,
    isSignificant,
    recommendedSampleSize,
  }
}
